/**
 * src/hooks/useServerTime.js — Server-synced Eastern time.
 *
 * Fetches the server's authoritative timestamp, calculates the offset
 * between client clock and server clock, then provides a `now()` function
 * that always returns the correct Eastern-synced time.
 *
 * Re-syncs every 60 seconds to stay accurate.
 * All consumers get identical time regardless of client timezone or OS clock.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const SYNC_INTERVAL_MS = 60_000; // Re-sync every 60s
const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function useServerTime() {
    const [offset, setOffset] = useState(0);
    const [synced, setSynced] = useState(false);
    const [easternOffsetMs, setEasternOffsetMs] = useState(0);
    const offsetRef = useRef(0);
    const easternOffsetRef = useRef(0);

    const sync = useCallback(async () => {
        try {
            const t0 = Date.now();
            const res = await fetch(`${API_URL}/time`, { credentials: 'include' });
            const t1 = Date.now();

            if (!res.ok) return;

            const data = await res.json();
            // Account for network latency (round-trip / 2)
            const latency = (t1 - t0) / 2;
            const serverNow = data.serverTime + latency;
            const clockOffset = serverNow - t1;

            offsetRef.current = clockOffset;
            easternOffsetRef.current = data.offsetMs || 0;
            setOffset(clockOffset);
            setEasternOffsetMs(data.offsetMs || 0);
            setSynced(true);
        } catch (err) {
            console.warn('[ServerTime] Sync failed:', err.message);
            // Keep last known offset — don't break the UI
        }
    }, []);

    useEffect(() => {
        sync();
        const interval = setInterval(sync, SYNC_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [sync]);

    /**
     * Get current server-synced time as a Date object (UTC).
     * This is the server's actual wall clock time.
     */
    const now = useCallback(() => {
        return new Date(Date.now() + offsetRef.current);
    }, []);

    /**
     * Get current time adjusted to Eastern timezone for display.
     * Returns a Date whose UTC methods give Eastern wall-clock values.
     */
    const easternNow = useCallback(() => {
        const serverUtc = Date.now() + offsetRef.current;
        return new Date(serverUtc + easternOffsetRef.current);
    }, []);

    /**
     * Get countdown seconds until next minute boundary (server time).
     */
    const getCountdown = useCallback(() => {
        const serverDate = new Date(Date.now() + offsetRef.current);
        return 60 - serverDate.getSeconds();
    }, []);

    return { now, easternNow, getCountdown, synced, offset, easternOffsetMs };
}
